import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import {
  SettingType,
  SIEM_HEC_TOKEN_MASK,
  type SiemConfig,
  siemForwardsAlerts,
  siemForwardsAuditLogs,
  SiemProtocol,
} from '@kirin/api-client/contracts/settings';

import { DestinationFields } from './DestinationFields';
import { ForwardedEventsField } from './ForwardedEventsField';
import { MinimumSeverityField } from './MinimumSeverityField';
import { ModalFooterActions } from './ModalFooterActions';
import { SiemNoteBox } from './SiemNoteBox';
import { TransportProtocolField } from './TransportProtocolField';
import { siemFormResolver } from './siemFormResolver';
import { siemProtocolDefaultPort } from './constants';
import type { SiemFormValues } from './types';
import { useUpsertOrgSetting } from '@/hooks/api/useOrgSettings';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import { useToast } from '@/hooks/use-toast';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';

/**
 * Build the SIEM setting config payload for the active protocol. HEC sends
 * `endpointUrl` + `hecToken`; syslog (tcp/udp) sends `host` + `port`.
 */
function buildSiemConfigPayload(
  values: SiemFormValues
): Record<string, unknown> {
  if (values.protocol === SiemProtocol.Hec) {
    return {
      protocol: values.protocol,
      endpointUrl: values.endpointUrl,
      hecToken: values.hecToken,
      insecureSkipVerify: values.insecureSkipVerify,
      minSeverity: values.minimumSeverity,
      forwardAlerts: values.forwardAlerts,
      forwardAuditLogs: values.forwardAuditLogs,
    };
  }
  return {
    protocol: values.protocol,
    host: values.host,
    port: values.port,
    minSeverity: values.minimumSeverity,
    forwardAlerts: values.forwardAlerts,
    forwardAuditLogs: values.forwardAuditLogs,
  };
}

interface ConnectSiemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  initialConfig?: Partial<SiemConfig>;
  /** When true, both the save and test connection actions are disabled. */
  readOnly?: boolean;
}

export function ConnectSiemModal({
  open,
  onOpenChange,
  organizationId,
  initialConfig,
  readOnly = false,
}: ConnectSiemModalProps) {
  const upsertMutation = useUpsertOrgSetting(organizationId, SettingType.Siem);
  const testMutation = useUpsertOrgSetting(organizationId, SettingType.Siem);
  const { toast } = useToast();
  const { markDirty, markClean } = useUnsavedChangeActions();
  const { changeScopeTo } = useScopeChangeAction();
  const [testStatus, setTestStatus] = useState<
    'idle' | 'testing' | 'success' | 'error'
  >('idle');
  const [testError, setTestError] = useState('');

  const scopeId = `siem-integration-${organizationId}`;

  const buildFormValues = useCallback(
    () => ({
      protocol: initialConfig?.protocol ?? SiemProtocol.Tcp,
      host: initialConfig?.host ?? '',
      port: initialConfig?.port ?? siemProtocolDefaultPort[SiemProtocol.Tcp],
      endpointUrl: initialConfig?.endpointUrl ?? '',
      // Editing an existing HEC config: GET redacts the token and returns
      // `hecTokenSet` instead, so pre-fill the mask sentinel — the field reads
      // as set (password dots) and, if left unchanged, the backend keeps the
      // stored token rather than requiring re-entry (KIR-4031).
      hecToken:
        initialConfig?.protocol === SiemProtocol.Hec &&
        initialConfig?.hecTokenSet
          ? SIEM_HEC_TOKEN_MASK
          : (initialConfig?.hecToken ?? ''),
      insecureSkipVerify: initialConfig?.insecureSkipVerify ?? false,
      minimumSeverity: initialConfig?.minSeverity ?? Severity.Medium,
      // Read the individual fields, not `initialConfig` itself: the dependency
      // array below is property-scoped, and referencing the whole object breaks
      // the compiler's memoization check.
      forwardAlerts: siemForwardsAlerts({
        forwardAlerts: initialConfig?.forwardAlerts,
      }),
      forwardAuditLogs: siemForwardsAuditLogs({
        forwardAuditLogs: initialConfig?.forwardAuditLogs,
      }),
    }),
    [
      initialConfig?.host,
      initialConfig?.minSeverity,
      initialConfig?.port,
      initialConfig?.protocol,
      initialConfig?.endpointUrl,
      initialConfig?.hecToken,
      initialConfig?.hecTokenSet,
      initialConfig?.insecureSkipVerify,
      initialConfig?.forwardAlerts,
      initialConfig?.forwardAuditLogs,
    ]
  );

  const form = useForm<SiemFormValues>({
    defaultValues: buildFormValues(),
    resolver: siemFormResolver,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { formState } = form;
  const protocol = useWatch({ control: form.control, name: 'protocol' });

  const resetFormValues = useCallback(() => {
    form.reset(buildFormValues());
  }, [buildFormValues, form]);

  const resetFormState = useCallback(() => {
    resetFormValues();
    setTestStatus('idle');
    setTestError('');
    markClean(scopeId);
  }, [markClean, resetFormValues, scopeId]);

  useEffect(() => {
    if (formState.isDirty) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }

    return () => {
      markClean(scopeId);
    };
  }, [formState.isDirty, markDirty, markClean, scopeId]);

  useEffect(() => {
    if (open && !formState.isDirty) {
      resetFormValues();
    }
  }, [open, formState.isDirty, resetFormValues]);

  const resetTestState = useCallback(() => {
    if (testStatus !== 'idle' || testError) {
      setTestStatus('idle');
      setTestError('');
    }
  }, [testError, testStatus]);

  const resolveErrorReason = () => {
    const fields: (keyof SiemFormValues)[] = [
      'port',
      'host',
      'endpointUrl',
      'hecToken',
    ];
    for (const field of fields) {
      const message = form.getFieldState(field).error?.message;
      if (message) {
        return message;
      }
    }
    return 'Invalid form values';
  };

  const handleTest = async () => {
    setTestError('');
    const isValid = await form.trigger();
    if (!isValid) {
      setTestStatus('error');
      setTestError(resolveErrorReason());
      return;
    }

    setTestStatus('testing');
    try {
      const values = form.getValues();
      const result = await testMutation.mutateAsync({
        body: {
          config: buildSiemConfigPayload(values),
        },
        params: { test: 'only' },
      });
      if (!('success' in result)) {
        setTestStatus('error');
        setTestError('Unexpected response from test endpoint');
        return;
      }
      if (!result.success) {
        setTestStatus('error');
        setTestError(result.error ?? 'Test failed');
        return;
      }
      setTestStatus('success');
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error';
      setTestStatus('error');
      setTestError(reason);
    }
  };

  const handleSubmit = form.handleSubmit(async (values): Promise<void> => {
    try {
      await upsertMutation.mutateAsync({
        body: {
          config: buildSiemConfigPayload(values),
        },
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { error_type: 'siem_save' },
        extra: { organizationId },
      });
      toast({
        title: 'Failed to save SIEM settings',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
      return;
    }
    setTestStatus('idle');
    setTestError('');
    form.reset(values, { keepValues: true });
    markClean(scopeId);
    toast({
      title: 'SIEM settings saved',
    });
    onOpenChange(false);
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      return;
    }

    if (!formState.isDirty) {
      onOpenChange(false);
      return;
    }

    changeScopeTo({
      from: scopeId,
      id: 'close-siem-integration-modal',
      title: 'Unsaved changes',
      body: 'You have unsaved changes that will be lost.',
      cancelLabel: 'Keep Editing',
      discardLabel: 'Discard',
      run: () => {
        resetFormState();
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md text-foreground top-[8vh] translate-y-0 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary">Connect SIEM</DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <TransportProtocolField onSensitiveChange={resetTestState} />
            <DestinationFields onSensitiveChange={resetTestState} />
            <MinimumSeverityField />
            <ForwardedEventsField />
            <SiemNoteBox protocol={protocol} />
            <ModalFooterActions
              onTest={handleTest}
              testStatus={testStatus}
              testError={testError}
              disableSave={
                readOnly || testStatus !== 'success' || upsertMutation.isPending
              }
              disableTest={readOnly}
              isTesting={testMutation.isPending || testStatus === 'testing'}
              isSaving={upsertMutation.isPending}
            />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
